import type { ReactNode } from 'react';

export type TParagraph = { kind: 'p'; text: ReactNode };
export type TNote = { kind: 'note'; text: string };
export type TBulletList = { kind: 'list'; items: ReactNode[] };
export type TDataTable = {
  kind: 'table';
  columns: { field: string; headerName: string }[];
  rows: { id: number; [key: string]: string | number }[];
  hideHeader?: boolean;
  boldFirstColumn?: boolean;
};
export type TSubSection = {
  kind: 'subsection';
  title: string;
  blocks: TBlock[];
};

export type TBlock =
  | TParagraph
  | TNote
  | TBulletList
  | TDataTable
  | TSubSection;

export type TSection = {
  id: string;
  tocLabel: string;
  heading: string;
  blocks: TBlock[];
};

export const SECTIONS: TSection[] = [
  {
    id: 'sec-0',
    tocLabel: '0. Naturaleza, Prelación y Exclusividad del Documento',
    heading: '0. Naturaleza, Prelación y Exclusividad del Documento',
    blocks: [
      {
        kind: 'p',
        text: 'Este Acuerdo Maestro de Nivel de Servicio (en adelante, el “Master ANS”) constituye el único documento de niveles de servicio aplicable a la relación entre Crehana y el Cliente. Reemplaza, deja sin efecto y prevalece sobre cualquier acuerdo de nivel de servicio, anexo técnico, tabla de tiempos o compromiso de soporte anterior, verbal o escrito, cualquiera sea su denominación o fecha.',
      },
      {
        kind: 'p',
        text: 'Salvo pacto expreso y por escrito en contrario, ningún otro documento, propuesta comercial, correo electrónico, artículo de centro de ayuda u oferta podrá modificar, ampliar o contradecir los compromisos aquí establecidos. En caso de conflicto entre este Master ANS y cualquier otro documento, prevalecerá este Master ANS en todo lo relativo a niveles de servicio.',
      },
      {
        kind: 'p',
        text: 'Este Master ANS se incorpora por referencia al Contrato principal suscrito entre las partes y forma parte integral del mismo. Aplica de manera uniforme a todos los contratos base, con independencia del plan o segmento contratado.',
      },
      {
        kind: 'note',
        text: 'Nota de prelación: para que la cláusula de exclusividad sea oponible, el Contrato principal debe remitir expresamente a este documento como el único ANS válido.',
      },
    ],
  },
  {
    id: 'sec-1',
    tocLabel: '1. Resumen General y Objeto',
    heading: '1. Resumen General y Objeto',
    blocks: [
      {
        kind: 'p',
        text: 'El propósito de este Acuerdo es establecer los elementos y compromisos necesarios para proporcionar un servicio de tecnología consistente y una entrega medible al Cliente por parte de Crehana como Proveedor de Servicios. Los objetivos son:',
      },
      {
        kind: 'list',
        items: [
          'Proporcionar una referencia clara sobre la propiedad del servicio, la responsabilidad y los roles de cada parte.',
          'Presentar al Cliente una descripción clara, concisa y medible de la prestación del servicio.',
          'Alinear las expectativas del Cliente con el soporte y la entrega real del servicio.',
        ],
      },
    ],
  },
  {
    id: 'sec-2',
    tocLabel: '2. Definiciones',
    heading: '2. Definiciones',
    blocks: [
      {
        kind: 'p',
        text: 'Para efectos de este Master ANS, los siguientes términos tendrán el significado que se indica:',
      },
      {
        kind: 'table',
        hideHeader: true,
        boldFirstColumn: true,
        columns: [
          { field: 'term', headerName: 'ANS / SLA' },
          {
            field: 'definition',
            headerName:
              'Acuerdo de Nivel de Servicio. Conjunto de compromisos medibles de disponibilidad y atención que Crehana se obliga a cumplir.',
          },
        ],
        rows: [
          {
            id: 1,
            term: 'ANS / SLA',
            definition:
              'Acuerdo de Nivel de Servicio. Conjunto de compromisos medibles de disponibilidad y atención que Crehana se obliga a cumplir.',
          },
          {
            id: 2,
            term: 'Incidente',
            definition:
              'Toda interrupción no planificada o reducción de la calidad de un servicio reportada por el Cliente y registrada en el sistema de gestión de Crehana.',
          },
          {
            id: 3,
            term: 'Requerimiento técnico',
            definition:
              'Solicitud operativa sobre datos o configuración (p. ej., actualización de base de usuarios, extracción de reportes personalizados, implementación de procesos).',
          },
          {
            id: 4,
            term: 'Ticket',
            definition:
              'Registro formal de un incidente o requerimiento al que se le asigna un número identificador único.',
          },
          {
            id: 5,
            term: 'Primera Respuesta',
            definition:
              'Primer contacto sustantivo del equipo de soporte acusando recibo y dando seguimiento a un ticket.',
          },
          {
            id: 6,
            term: 'Tiempo de resolución',
            definition:
              'Período entre el registro del ticket y la entrega de la solución o respuesta de cierre.',
          },
          {
            id: 7,
            term: 'Días hábiles',
            definition:
              'Días laborables de lunes a viernes, excluyendo festivos aplicables, en zona horaria UTC-5.',
          },
          {
            id: 8,
            term: 'TDSR',
            definition: 'Tiempo de disponibilidad del servicio requerido.',
          },
          {
            id: 9,
            term: 'TNDT',
            definition: 'Tiempo de no disponibilidad tolerado.',
          },
          {
            id: 10,
            term: 'Mantenimiento programado',
            definition:
              'Ventana planificada y notificada con anticipación durante la cual el servicio puede no estar disponible; excluida del cálculo de disponibilidad.',
          },
        ],
      },
    ],
  },
  {
    id: 'sec-3',
    tocLabel: '3. Partes Interesadas',
    heading: '3. Partes Interesadas',
    blocks: [
      {
        kind: 'table',
        hideHeader: true,
        boldFirstColumn: true,
        columns: [
          { field: 'role', headerName: 'Proveedor de servicios' },
          { field: 'detail', headerName: 'Crehana (“Proveedor”)' },
        ],
        rows: [
          {
            id: 1,
            role: 'Proveedor de servicios',
            detail: 'Crehana (“Proveedor”)',
          },
          {
            id: 2,
            role: 'Cliente',
            detail: 'El “Cliente” identificado en el Contrato principal',
          },
          {
            id: 3,
            role: 'Contacto',
            detail:
              'Account Representative / Customer Success Manager — gestión vía correo',
          },
          {
            id: 4,
            role: 'Cadencia',
            detail:
              'On-demand: el Cliente puede solicitar reuniones de seguimiento cuando lo requiera; no existe una periodicidad fija predefinida.',
          },
          {
            id: 5,
            role: 'Business Reviews',
            detail:
              'Dashboard self-service: el Cliente monitorea sus resultados cuando lo requiera',
          },
        ],
      },
    ],
  },
  {
    id: 'sec-4',
    tocLabel: '4. Vigencia y Revisión Periódica',
    heading: '4. Vigencia y Revisión Periódica',
    blocks: [
      {
        kind: 'p',
        text: 'Este Master ANS es válido a partir de la Fecha de Activación de las Licencias establecida en el Contrato y permanecerá vigente mientras el Contrato principal se encuentre en vigor.',
      },
      {
        kind: 'p',
        text: 'Crehana es responsable de facilitar las revisiones periódicas. El contenido puede modificarse según sea necesario, siempre que se obtenga el acuerdo mutuo por escrito de las partes principales y se comunique a todas las partes afectadas.',
      },
      {
        kind: 'table',
        hideHeader: true,
        boldFirstColumn: true,
        columns: [
          { field: 'key', headerName: 'Responsable de la revisión' },
          { field: 'value', headerName: 'Crehana' },
        ],
        rows: [
          {
            id: 1,
            key: 'Responsable de la revisión',
            value: 'Crehana',
          },
          {
            id: 2,
            key: 'Periodo de revisión',
            value: 'Anual',
          },
          {
            id: 3,
            key: 'Próxima revisión',
            value: 'Enero 2027',
          },
        ],
      },
    ],
  },
  {
    id: 'sec-5',
    tocLabel: '5. Acuerdo de Servicio',
    heading: '5. Acuerdo de Servicio',
    blocks: [
      {
        kind: 'subsection',
        title: '5.1 Disponibilidad del Servicio Contratado',
        blocks: [
          {
            kind: 'p',
            text: 'Para la solución contratada en modelo SaaS se establecen los siguientes indicadores de disponibilidad:',
          },
          {
            kind: 'table',
            columns: [
              { field: 'indicator', headerName: 'Indicador' },
              { field: 'value', headerName: 'Valor' },
            ],
            rows: [
              {
                id: 1,
                indicator: 'TDSR — Tiempo de disponibilidad requerido',
                value: '99.5%',
              },
              {
                id: 2,
                indicator: 'TNDT — Tiempo de no disponibilidad tolerado',
                value: '0.5%',
              },
            ],
          },
          {
            kind: 'note',
            text: 'TDSR + TNDT corresponde al 100% del tiempo disponible deseable del producto.',
          },
        ],
      },
      {
        kind: 'subsection',
        title: '5.2 Prioridades y Tiempos de Resolución',
        blocks: [
          {
            kind: 'p',
            text: 'Los tiempos de resolución de bugs y consultas se rigen por la siguiente matriz de prioridad. Se garantiza una Primera Respuesta en menos de 24 horas hábiles para todos los casos.',
          },
          {
            kind: 'table',
            columns: [
              { field: 'priority', headerName: 'Prioridad' },
              { field: 'time', headerName: 'Tiempo de resolución' },
            ],
            rows: [
              { id: 1, priority: 'Crítico', time: '≤ 4 horas' },
              {
                id: 2,
                priority: 'Alto',
                time: '≤ 3 a 6 días hábiles',
              },
              {
                id: 3,
                priority: 'Medio',
                time: '≤ 5 a 8 días hábiles',
              },
              {
                id: 4,
                priority: 'Bajo',
                time: '≤ 7 a 10 días hábiles',
              },
            ],
          },
          {
            kind: 'note',
            text: 'Los tiempos se miden en días hábiles, excepto el nivel Crítico (en horas). La definición de cada nivel de prioridad se detalla en el Anexo A.',
          },
        ],
      },
      {
        kind: 'subsection',
        title: '5.3 Requerimientos Técnicos y Nuevos Desarrollos',
        blocks: [
          {
            kind: 'table',
            columns: [
              { field: 'type', headerName: 'Tipo' },
              { field: 'time', headerName: 'Tiempo estimado' },
            ],
            rows: [
              {
                id: 1,
                type: 'Pedido técnico',
                time: '≤ 5 a 10 días hábiles',
              },
              {
                id: 2,
                type: 'Pedido de desarrollo nuevo',
                time: 'Sujeto a evaluación de producto y roadmap',
              },
              {
                id: 3,
                type: 'Pedido de contenidos',
                time: 'Sujeto a evaluación',
              },
              {
                id: 4,
                type: 'New Feature Request — Primera Respuesta',
                time: '≤ 3 días hábiles',
              },
            ],
          },
          {
            kind: 'note',
            text: 'Los reportes autodescargables por el administrador no se consideran requerimientos técnicos.',
          },
        ],
      },
      {
        kind: 'subsection',
        title: '5.4 Cómo Reportar un Incidente',
        blocks: [
          {
            kind: 'p',
            text: 'El Cliente puede reportar un incidente a través de cualquiera de estos canales:',
          },
          {
            kind: 'list',
            items: [
              <>
                Correo: <strong>soportelearning@crehana.com</strong>
              </>,
              'Chat: web messenger en la esquina inferior derecha de la plataforma',
              'Chatbot / Help Center: disponible 24/7',
            ],
          },
          {
            kind: 'p',
            text: 'Para una atención más rápida, el Cliente debe incluir siempre: correo del usuario afectado, navegador y sistema operativo, descripción del error y capturas de pantalla.',
          },
          {
            kind: 'p',
            text: 'Los tiempos comienzan a contar desde el registro del incidente y la generación del número de ticket.',
          },
        ],
      },
      {
        kind: 'subsection',
        title: '5.5 Responsabilidades del Cliente',
        blocks: [
          {
            kind: 'list',
            items: [
              'Pago de los costos de soporte en el intervalo acordado.',
              'Disponibilidad de sus representantes para responder consultas del equipo al resolver un incidente.',
              'Responder dentro de 5 días hábiles para evitar el cierre automático del ticket.',
            ],
          },
        ],
      },
      {
        kind: 'subsection',
        title: '5.6 Responsabilidades de Crehana',
        blocks: [
          {
            kind: 'list',
            items: [
              'Cumplir con los tiempos de respuesta y resolución establecidos en este acuerdo.',
              'Notificar al Cliente sobre todo mantenimiento programado con anticipación.',
              'Garantizar una primera respuesta en menos de 24 horas hábiles.',
            ],
          },
        ],
      },
      {
        kind: 'subsection',
        title: '5.7 Supuestos y Exclusiones del Servicio',
        blocks: [
          {
            kind: 'list',
            items: [
              'Los cambios en los servicios se comunicarán y documentarán a todas las partes interesadas.',
              'Para requerimientos complejos, el tiempo puede extenderse con previo aviso.',
            ],
          },
          {
            kind: 'p',
            text: 'No se computan contra los ANS ni generan incumplimiento de Crehana los eventos derivados de: mantenimiento programado y notificado; fuerza mayor o caso fortuito; fallas de conectividad, software o hardware del Cliente; uso indebido de la plataforma; o indisponibilidad de servicios de terceros fuera del control de Crehana.',
          },
        ],
      },
    ],
  },
  {
    id: 'sec-6',
    tocLabel: '6. Gestión de Servicios',
    heading: '6. Gestión de Servicios',
    blocks: [
      {
        kind: 'subsection',
        title: '6.1 Canales de Soporte y Horarios',
        blocks: [
          {
            kind: 'table',
            columns: [
              { field: 'channel', headerName: 'Canal' },
              { field: 'availability', headerName: 'Disponibilidad' },
            ],
            rows: [
              {
                id: 1,
                channel: 'Chatbot / Help Center',
                availability: '24/7',
              },
              {
                id: 2,
                channel: 'Soporte humano (chat / email)',
                availability: 'Lunes a viernes, 9:00 a.m. – 6:00 p.m. (UTC-5)',
              },
            ],
          },
        ],
      },
      {
        kind: 'subsection',
        title: '6.2 Flujo de Atención de Problemas Técnicos',
        blocks: [
          {
            kind: 'p',
            text: 'Los tiempos cuentan desde que el incidente queda registrado y se genera el número de ticket.',
          },
          {
            kind: 'list',
            items: [
              'Correo N1 — Recepción y encaminamiento al equipo de atención inicial.',
              'Soporte N2 — Segundo análisis y creación del ticket.',
              'Soporte N3 — El equipo de tecnología atiende bugs o casos de alta complejidad.',
              'Validación — Cierre interno y validación de la resolución.',
              'Cierre y notificación — Verificación, cierre del ticket y notificación al Cliente.',
            ],
          },
        ],
      },
      {
        kind: 'subsection',
        title: '6.3 Acceso y Visibilidad de Tickets',
        blocks: [
          {
            kind: 'list',
            items: [
              'Plataforma: acceso directo para levantar tickets.',
              'Visión y control: todas las solicitudes se comparten con los accesos de la empresa del Cliente y con su CSM.',
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'sec-7',
    tocLabel: '7. Información y Confidencialidad',
    heading: '7. Información y Confidencialidad',
    blocks: [
      {
        kind: 'p',
        text: 'Crehana protege la confidencialidad de los datos de sus clientes conforme a sus políticas de seguridad.',
      },
      {
        kind: 'list',
        items: [
          'Período de almacenamiento: durante la vigencia del contrato y el período adicional estipulado.',
          'Recuperación de información: a solicitud, a través del canal de soporte o del CSM asignado.',
        ],
      },
    ],
  },
  {
    id: 'sec-8',
    tocLabel: '8. Cierre de Tickets y Terminación de Contrato',
    heading: '8. Cierre de Tickets y Terminación de Contrato',
    blocks: [
      {
        kind: 'subsection',
        title: '8.1 Cierre Automático por Inactividad',
        blocks: [
          {
            kind: 'list',
            items: [
              'Si se solicita información adicional sin respuesta, el ticket pasa a estado Pendiente.',
              'Las conversaciones sin respuesta (no convertidas en ticket) se cierran en 3 horas.',
              'Sin actividad por 5 días hábiles, el caso se cierra automáticamente.',
              'Si el problema persiste, el Cliente puede responder al correo de notificación dentro de los 3 días hábiles posteriores al cierre. Después de ese plazo, será necesario crear una nueva solicitud.',
            ],
          },
        ],
      },
      {
        kind: 'subsection',
        title: '8.2 Terminación de Contrato',
        blocks: [
          {
            kind: 'list',
            items: [
              'La información del Cliente se resguarda por el período establecido en el contrato.',
              'Al finalizar el contrato, los accesos de los usuarios del Cliente se desactivan conforme al proceso de offboarding de Crehana.',
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'sec-9',
    tocLabel: '9. Limitación de Responsabilidad',
    heading: '9. Limitación de Responsabilidad',
    blocks: [
      {
        kind: 'p',
        text: 'Este Master ANS no contempla penalidades ni créditos de servicio. El incumplimiento de los niveles de servicio aquí establecidos se gestionará conforme a los mecanismos generales de responsabilidad del Contrato principal.',
      },
    ],
  },
  {
    id: 'sec-10',
    tocLabel: '10. Ley Aplicable, Jurisdicción y Resolución de Controversias',
    heading: '10. Ley Aplicable, Jurisdicción y Resolución de Controversias',
    blocks: [
      {
        kind: 'p',
        text: 'La ley aplicable, el foro o tribunales competentes (o cláusula de arbitraje) y el idioma rector se regirán por lo establecido en el Contrato principal.',
      },
    ],
  },
  {
    id: 'sec-11',
    tocLabel: '11. Modificaciones',
    heading: '11. Modificaciones',
    blocks: [
      {
        kind: 'p',
        text: 'Cualquier modificación a este Master ANS requerirá el acuerdo mutuo por escrito de las partes principales y deberá registrarse en el Control de Versiones de la portada.',
      },
      {
        kind: 'p',
        text: 'Ninguna comunicación informal modifica los compromisos aquí establecidos.',
      },
    ],
  },
  {
    id: 'sec-anexo-a',
    tocLabel: 'Anexo A — Definiciones de Prioridad',
    heading: 'Anexo A — Definiciones de Prioridad',
    blocks: [
      {
        kind: 'subsection',
        title: 'Crítico — Interrupción completa de sistemas esenciales',
        blocks: [
          {
            kind: 'list',
            items: [
              'La plataforma está caída para todos los usuarios; nadie puede ingresar.',
              'Error masivo al cierre de evaluaciones que impide completar el proceso.',
              'Caída del SSO del lado de Crehana que bloquea el acceso total.',
            ],
          },
        ],
      },
      {
        kind: 'subsection',
        title: 'Alto — Departamento completo sin acceso a sistema esencial',
        blocks: [
          {
            kind: 'list',
            items: [
              'Toda un área no puede visualizar sus evaluaciones o metas asignadas.',
              'El administrador principal perdió acceso y no puede gestionar la plataforma.',
              'Todos los usuarios de una empresa no pueden autenticarse.',
            ],
          },
        ],
      },
      {
        kind: 'subsection',
        title: 'Medio — Degradación de funciones sin detener la operación',
        blocks: [
          {
            kind: 'list',
            items: [
              'El módulo de reportes no genera descarga, pero los usuarios pueden navegar.',
              'Algunos usuarios no visualizan su feedback, pero la plataforma opera con normalidad.',
              'Un módulo muestra error intermitente sin bloquear el flujo principal.',
            ],
          },
        ],
      },
      {
        kind: 'subsection',
        title: 'Bajo — Problemas aislados sin impacto crucial al negocio',
        blocks: [
          {
            kind: 'list',
            items: [
              'Un usuario específico no puede ver su evaluación, pero sus compañeros sí.',
              'Error visual en la interfaz que no afecta la funcionalidad.',
              'Un colaborador no recibió el correo de bienvenida, pero puede ingresar.',
            ],
          },
        ],
      },
      {
        kind: 'note',
        text: 'Fuente de valores y definiciones: Acuerdo de Nivel de Servicio (ANS) — Crehana (Centro de Ayuda).',
      },
    ],
  },
];
